import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import { MemberProfileClient, SharedProfileDTO } from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import publicAxiosInstance from '../services/publicAxiosInstance';
import { isValidGuid } from '../utils/validateGuidUtil';

// Sharing the member's profile. The same model as sharing a Courses Played list
// (useCoursesPlayedShare), with its own code: the member's switch goes through axiosInstance
// (it needs their token), the public read through publicAxiosInstance, so a stale session on the
// visitor's browser never turns "open a shared profile" into "sign in first".
const apiClient = new MemberProfileClient(API_BASE_URL, axiosInstance);
const publicClient = new MemberProfileClient(API_BASE_URL, publicAxiosInstance);

const MY_SHARE_KEY = ['myProfileShare'];

// The public address of a shared profile, built from the page's own origin like a shared list's.
export const sharedProfileUrl = (shareId: string): string =>
  `${window.location.origin}/shared/profile/${shareId.toLowerCase()}`;

// ---- The member's own sharing switch (signed in) ----------------------------------------------

// The member's profile share code, or null while their profile is not shared.
export const useMyProfileShare = (
  enabled: boolean,
): UseQueryResult<string | null> =>
  useQuery<string | null>({
    queryKey: MY_SHARE_KEY,
    queryFn: async () => {
      const response = await apiClient.getMyProfileShare();
      return response?.data?.shareId ?? null;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

// Turns sharing on and resolves with the code. Idempotent on the backend.
export const useShareMyProfile = (): UseMutationResult<
  string | null,
  unknown,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.shareMyProfile();
      return response?.data?.shareId ?? null;
    },
    onSuccess: (shareId) => {
      queryClient.setQueryData(MY_SHARE_KEY, shareId);
    },
  });
};

// Turns sharing off. The old link stops working at once.
export const useStopSharingMyProfile = (): UseMutationResult<
  void,
  unknown,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.stopSharingMyProfile();
    },
    onSuccess: () => {
      queryClient.setQueryData(MY_SHARE_KEY, null);
    },
  });
};

// ---- The public view (no sign-in) --------------------------------------------------------------

export type SharedProfileState =
  | { status: 'invalid' }
  | { status: 'loading' }
  | { status: 'notShared' }
  | { status: 'error' }
  | { status: 'ready'; profile: SharedProfileDTO };

// A shared profile by its code, for anyone. As with a shared list, a malformed code answers "not
// a valid link" without a request, and a well-formed code nobody holds answers "not shared".
export const useSharedProfile = (
  shareId: string | undefined,
): SharedProfileState => {
  const code = shareId?.trim() ?? '';
  const isValid = isValidGuid(code);

  const query = useQuery<SharedProfileDTO | null>({
    queryKey: ['sharedProfile', code.toLowerCase()],
    queryFn: async () => {
      try {
        const response = await publicClient.getSharedProfile(code);
        return response?.data ?? null;
      } catch (error: unknown) {
        if ((error as { status?: number })?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: isValid,
    staleTime: 60 * 1000,
    retry: false,
  });

  if (!isValid) {
    return { status: 'invalid' };
  }
  if (query.isLoading) {
    return { status: 'loading' };
  }
  if (query.isError) {
    return { status: 'error' };
  }
  if (query.data == null) {
    return { status: 'notShared' };
  }
  return { status: 'ready', profile: query.data };
};
